/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Input, Icon } from "@coreai/component-library";
import { useTheme } from "@emotion/react";
import { useToast } from "@coreai/component-library";
import { useLanguage } from "LanguageContext";
import { useOutsideAlerter } from "../../../hooks/useOutsideAlerter";
import EditProjectDialog from "../../../components/MainComponents/Projects/EditProjectDialog";
import AddProjectDialog from "../../../components/MainComponents/Analyses/FormComponents/AlertDialogAddProject";
import { patchProject } from "../../../services/projectsService";

interface AutocompleteAnalysisProps {
  data: any;
  setAutocompleteValue: (value: string) => void;
  focuseNext: () => void;
  autocompleteValue?: string;
  autocompleteLable?: string;
  autocompletePlaceholder?: string;
  addnewButtonPrefix?: string;
  messageUpdate?: (args: {
    showMsg: boolean;
    oldName: string | null;
    newName: string;
    id: string;
  }) => void;
  props?: any;
}

export default function AutocompleteAnalysis(
  props: Readonly<AutocompleteAnalysisProps>
) {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(
    props.autocompleteValue || ""
  );
  const [filteredList, setFilteredList] = useState<
    { value: string; isEdit: boolean; name: string; advertiser: string; id: string }[]
  >([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const [outsideSideMenu] = useOutsideAlerter(dropdownWrapperRef);
  const [editInput, setEditInput] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const isEditRef = useRef(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState("");
  const [isAddNew, setIsAddNew] = useState(false);
  const [newProject, setNewProject] = useState<string>("");
  const searchInputRef = React.useRef<HTMLInputElement>(null); // Explicitly type the ref
  
  const { toast } = useToast();
  const { t } = useLanguage();

  const dropdownStyles = css`
    position: absolute;
    background: white;
    border: 1px solid ${theme.colors.strokeGray};
    border-top: none;
    border-radius: 0 0 ${theme.radius.xs} ${theme.radius.xs};
    width: 100%;
    z-index: 1000;
    box-shadow: ${theme.shadows.medium};

    .suggestion-item,
    .suggest-new {
      background: transparent;
    }

    .suggestion-item:hover,
    .suggest-new:hover {
      background: #f2f6f8;
    }
  `;

  const suggestionsContainerStyle = css`
    max-height: 150px;
    overflow-y: auto;
  `;

  const dropdownItemStyle = css`
    padding: ${theme.spacing.sm};
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    line-height: ${theme.typography.bodyMedium.lineHeight};
    color: ${theme.colors.textMedium};
    cursor: pointer;
    height: 60px;
  `;

  const addNewButtonStyle = css`
    padding: ${theme.spacing.md};
    border-radius: 0 0 ${theme.radius.xs} ${theme.radius.xs};
    cursor: pointer;
    color: ${theme.colors.primary};
    background-color: ${theme.colors.white};
  `;

  const leftIconStyle = css`
    position: absolute;
    left: 3px;
    top: 60%;
    transform: translateY(-50%);
    cursor: pointer;
  `;

  const rightIconStyle = css`
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    margin: 3px;
    cursor: pointer;
  `;

  const labelStyle = css`
    display: flex;
    height: 48px;
    flex: 8;
    border-radius: 8px;
    justify-content: flex-start;
    align-items: center;
    padding-left: 5px;
  `;

  const labelEditStyle = css`
    flex: 1;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  useEffect(() => {
    if (outsideSideMenu) {
      setShowDropdown(false);
    }
  }, [outsideSideMenu]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const value = e.target.value;
    const filtered = props.data
      .filter((item: any) =>
        `${item?.advertiser?.toLowerCase()} - ${item?.name?.toLowerCase()}`.includes(
          value.toLowerCase()
        )
      )
      .map((item: any) => ({
        value: item?.name,
        isEdit: false,
        name: item?.name,
        advertiser: item?.advertiser,
        id: item?.id,
      }));

    setFilteredList(filtered);
    setInputValue(value);
    props.setAutocompleteValue(value);
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: any) => {
    setInputValue(`${suggestion?.name} - ${suggestion?.advertiser}`);
    setShowDropdown(false);
    props.setAutocompleteValue(suggestion);
  };

  const handleAddClick = () => {
    handleParentDialog('hide');
    setIsAddNew(true);
  };

  const handleEditClick = (
    e: React.MouseEvent,
    data: { value: string; name: string; advertiser: string; id: string }
  ) => {
    e.preventDefault();
    handleParentDialog('hide');
    isEditRef.current = true;
    props.setAutocompleteValue(data?.id);
    setEditInput(data.value);
    setIsEditOpen(true);
    setEditData(`${data?.name}-${data?.advertiser}`);
    const updatedList = filteredList.map((item) => ({
      ...item,
      isEdit: item.value === data.value,
    }));

    setFilteredList(updatedList);
    setShowDropdown(true);

    setTimeout(() => {
      editInputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setInputValue("");
    props.setAutocompleteValue("");
    setShowDropdown(false);
  };

  const onEdit = async (projectId: string, projData: any) => {
    try {
      await patchProject(projectId, projData);
      const displayName = `${projData.advertiser} - ${projData.name}`;
      props.setAutocompleteValue(displayName);
      setInputValue(displayName);
      toast({
        variant: "success",
        title: t("project_toast_edit_success"),
      });
    } catch (error: any) {
      if (error?.status === 400) {
        toast({
          variant: "error",
          title: t("error_title"),
          description: t("save_project_toast_error_message", {
            projectName: projData.name,
            advertiserName: projData.advertiser,
          }),
        });
      }
      console.error("Error editing project:", error);
    }
  };

  const onCloseDialog = () => {
    handleParentDialog('show');
    setIsEditOpen(false);
  };

  const updateProject = (setProject: string) => {
    setNewProject(setProject);
    props.setAutocompleteValue(setProject);
  };

  const handleNewProject = (newProj: any) => {
    handleSuggestionClick(newProj);
  };
  const handleSearch = () =>{
    searchInputRef.current?.focus();
  }
  const handleParentDialog = (showHide:string) => {
    if (showHide === 'hide') {
      document.querySelectorAll("body > div[role='alertdialog'], body > div[data-state='open']").forEach((el) => {
        el.classList.add("hide-dialog");
      });
    } else if (showHide === 'show') {
      document.querySelectorAll("body > div.hide-dialog").forEach((el) => {
        el.classList.remove("hide-dialog");
      });
    }
  }
  return (
    <>
      {isEditOpen && (
        <EditProjectDialog
          project={props.data.find(
            (item: any) => `${item.name}-${item.advertiser}` === editData
          )}
          open={true}
          onClose={onCloseDialog}
          onEdit={onEdit}
          openBy='analysis'
        />
      )}
      {isAddNew && (
        <AddProjectDialog
          open={isAddNew}
          onClose={() =>{
            handleParentDialog('show');
            setIsAddNew(false);            
          } }
          suggestions={props?.data}
          saveProject={updateProject}
          defaultAdvertiser={inputValue}
          newProject={handleNewProject}
        />
      )}

      <div css={css`position: relative; width: 100%;`}>
        <div className="cai-flex cai-flex-col cai-gap-y-xs">
          <label className="cai-text-primary cai-font-medium">
            {t("alertDialog_add_project_label_project")}{" "}
          </label>

          <div className="cai-group cai-flex cai-items-center cai-px-lg cai-py-md cai-border cai-rounded-md cai-relative">
            <div css={leftIconStyle} onClick={(e) => { e.stopPropagation(); handleSearch(); }}>
              <Icon name="search" size="xs" />
            </div>

            <input
              ref={searchInputRef}
              type="text"
              className="cai-w-full cai-outline-none cai-text-body2 cai-placeholder-tertiary"
              placeholder={props.autocompletePlaceholder || ""}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputChange}
              css={css`
                padding-left: 15px;
                padding-right: 30px;
              `}
              autoComplete="off"
              {...props.props}
            />

            <div
              css={rightIconStyle}
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <Icon name="x-close" size="xs" />
            </div>
          </div>
        </div>

        {showDropdown && (
          <div ref={dropdownWrapperRef} css={dropdownStyles}>
            <div css={suggestionsContainerStyle}>
              {filteredList.map((item, idx) => (
                <div
                  key={`${item.value}-${idx}`}
                  className="suggestion-item"
                  css={dropdownItemStyle}
                  onMouseEnter={() => setHovered(idx + 1 || null)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div css={labelStyle}>
                    <span onClick={() => handleSuggestionClick(item)}>
                      {item?.name} - {item?.advertiser}
                    </span>
                  </div>

                  {idx + 1 === hovered && (
                    <div css={labelEditStyle}>
                      <Icon
                        name="edit-01"
                        size="xs"
                        onClick={(e) => handleEditClick(e, item)}
                      />
                    </div>
                  )}
                </div>
              ))}
              <div
                  key="add-new"
                  className="suggestion-item"
                  css={dropdownItemStyle}
                >
                  <div css={labelStyle}>
                    <span onClick={handleAddClick}>
                      {t("add_project_from_dropdown")}
                    </span>
                  </div>
                </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
  